import { type MouseEvent, useCallback } from 'react';

import { Box, Dialog } from '@mui/material';
import { useSearchParams } from 'react-router';

import FactsheetView from '@/features/Factsheet/components/FactsheetView';
import {
  ADDITIONAL_TABS_PARAM_NAME,
  FACTSHEET_PARAM_NAME,
  FACTSHEET_TAB_PARAM_NAME
} from '@/features/Factsheet/constants';

interface FactsheetDialogProps {
  code: string;
  open: boolean;
}

const FactsheetDialog = ({ code, open }: FactsheetDialogProps) => {
  const [, setUrlSearchParams] = useSearchParams();

  const handleClose = (): void => {
    setUrlSearchParams(searchParams => {
      searchParams.delete(FACTSHEET_TAB_PARAM_NAME);
      searchParams.delete(FACTSHEET_PARAM_NAME);
      searchParams.delete(ADDITIONAL_TABS_PARAM_NAME);
      return searchParams;
    });
  };

  const setAdditionalTabsParam = useCallback(
    (tabs: string[]) => {
      setUrlSearchParams(searchParams => {
        if (tabs.length === 0) {
          searchParams.delete(ADDITIONAL_TABS_PARAM_NAME);
        } else {
          searchParams.set(ADDITIONAL_TABS_PARAM_NAME, tabs.join(','));
        }
        if (searchParams.get(FACTSHEET_TAB_PARAM_NAME) !== 'Flowchart')
          searchParams.delete(FACTSHEET_TAB_PARAM_NAME);
        return searchParams;
      });
    },
    [setUrlSearchParams]
  );

  // Handle backdrop click - close only if clicking directly on the backdrop (not children)
  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  // Don't render anything when closed - this ensures all state is cleared on close
  if (!open) {
    return null;
  }

  return (
    <Dialog
      fullScreen
      open={open}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          backgroundColor: '#12163fcc'
        }}
        onClick={handleBackdropClick}
      >
        <FactsheetView
          factsheetKeys={code}
          setAdditionalTabsParam={setAdditionalTabsParam}
          onDialogClose={handleClose}
        />
      </Box>
    </Dialog>
  );
};

export default FactsheetDialog;
